import React, { useState } from "react";
import Select from "react-select";
import { Modal, ModalBody, ModalHeader, Button, Input, Label, Spinner, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { toast } from "react-toastify";
import useFbTokenSetup from "../../Hooks/useFbTokenSetup";
import useFbTokens from "../../Hooks/useFbTokens";

const FbTokenSetupModal = ({ isOpen, toggle, refresh }) => {
  const { loading: setupLoading, pages, userTokens, fetchPages, savePages, extendUserToken } = useFbTokenSetup();
  const { autoConnect } = useFbTokens();

  const [activeTab, setActiveTab] = useState("auto");
  const [type, setType] = useState("user");
  const [accessToken, setAccessToken] = useState("");
  const [autoAccessToken, setAutoAccessToken] = useState("");
  const [autoLoading, setAutoLoading] = useState(false);
  const [selectedUserToken, setSelectedUserToken] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);

  const handleAutoConnect = async () => {
    if (!autoAccessToken.trim()) {
      toast.error("Please paste an Access Token");
      return;
    }

    try {
      setAutoLoading(true);
      const res = await autoConnect(autoAccessToken.trim());
      toast.success(res?.message || "Facebook setup completed automatically!");
      setAutoAccessToken("");
      refresh?.();
      toggle();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Auto Setup Failed");
    } finally {
      setAutoLoading(false);
    }
  };

  const handleExtend = async () => {
    try {
      const res = await extendUserToken(accessToken);
      toast.success(res?.message || "User Token saved!");
      refresh?.();
      toggle();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message);
    }
  };

  const handleFetchPages = async (option) => {
    setSelectedUserToken(option);
    await fetchPages(option.value);
  };

  const handleSavePages = async () => {
    try {
      await savePages(
        selectedUserToken.value,
        selectedPages.map((p) => p.raw)
      );
      toast.success("Pages Saved Successfully");
      refresh?.();
      toggle();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>
        Facebook Integration & Token Setup
      </ModalHeader>

      <ModalBody>
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={activeTab === "auto" ? "active fw-bold text-primary" : "text-muted"}
              style={{ cursor: "pointer" }}
              onClick={() => setActiveTab("auto")}
            >
              ⚡ 1-Click Auto Setup (Recommended)
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={activeTab === "manual" ? "active fw-bold text-primary" : "text-muted"}
              style={{ cursor: "pointer" }}
              onClick={() => setActiveTab("manual")}
            >
              🛠️ Manual Step-by-Step
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="auto">
            <div className="alert alert-info border-0 mb-3">
              <strong>🚀 Automatic Setup:</strong> Paste any valid Facebook User Access Token (short-lived or Graph Explorer token). The system will automatically:
              <ol className="mb-0 mt-2 ps-3">
                <li>Exchange it for a <strong>60-day Long-Lived Token</strong></li>
                <li>Save & activate your Facebook User Account</li>
                <li>Fetch and import <strong>ALL Facebook Pages</strong> with <strong>Never-Expiring Tokens</strong></li>
                <li>Trigger immediate Lead Synchronization</li>
              </ol>
            </div>

            <div className="mb-3">
              <Label>Access Token</Label>
              <Input
                type="textarea"
                rows="5"
                placeholder="Paste Access Token here..."
                value={autoAccessToken}
                onChange={(e) => setAutoAccessToken(e.target.value)}
              />
            </div>

            <Button
              color="primary"
              size="lg"
              className="w-100"
              disabled={autoLoading || !autoAccessToken.trim()}
              onClick={handleAutoConnect}
            >
              {autoLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing 60-Day Exchange & Importing Pages...
                </>
              ) : (
                <>
                  <i className="mdi mdi-flash me-1" />
                  Auto Setup Everything (User & Pages)
                </>
              )}
            </Button>
          </TabPane>

          <TabPane tabId="manual">
            <div className="mb-3">
              <Label>Type</Label>
              <Input
                type="select"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="user">User Token</option>
                <option value="page">Page Token</option>
              </Input>
            </div>

            {type === "user" && (
              <>
                <div className="mb-3">
                  <Label>Paste User Token</Label>
                  <Input
                    type="textarea"
                    rows="6"
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                  />
                </div>

                <Button
                  color="primary"
                  disabled={setupLoading}
                  onClick={handleExtend}
                >
                  Save User Token
                </Button>
              </>
            )}

            {type === "page" && (
              <>
                <div className="mb-3">
                  <Label>Select User Token</Label>
                  <Select
                    options={userTokens.map((u) => ({
                      label: `${u.name} (${u.refId})`,
                      value: u._id,
                    }))}
                    onChange={handleFetchPages}
                  />
                </div>

                {!!pages.length && (
                  <div className="mb-3">
                    <Label>Select Pages</Label>
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      options={pages.map((p) => ({
                        label: `${p.name} (${p.id})`,
                        value: p.id,
                        raw: p,
                      }))}
                      onChange={setSelectedPages}
                    />
                  </div>
                )}

                {!!selectedPages.length && (
                  <Button
                    color="primary"
                    disabled={setupLoading}
                    onClick={handleSavePages}
                  >
                    Save Pages
                  </Button>
                )}
              </>
            )}
          </TabPane>
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

export default FbTokenSetupModal;